import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  EntityPropertyChanges,
  formatAuditLogDelta,
  getAuditLogDeltaClassName,
  IEntityPropertyArrayPrimitive,
  IEntityPropertyPrimitive,
  RydeUiUnit,
} from "@ryde/shared";

import { EntityPropertyItemsChange } from "./entity-property-items-change";
import { SearchHighlight } from "./search-highlight";
import { useEntityPropertyValue } from "./use-entity-property-value";

interface IEntityPropertiesValueProps {
  entity?: RydeUiUnit;
  entityProperty: IEntityPropertyPrimitive | IEntityPropertyArrayPrimitive;
}

export const EntityPropertyValue = ({
  entity,
  entityProperty,
}: IEntityPropertiesValueProps): ReactNode => {
  const { t } = useTranslation();
  const {
    isHidden,
    propertyKey,
    rootClassName,
    keyClassName,
    oldText,
    removedText,
    newText,
    numericDelta,
  } = useEntityPropertyValue({ entity, entityProperty });
  const { changeType, items } = entityProperty;

  if (isHidden) {
    return null;
  }

  return (
    <div className={rootClassName}>
      <div className={keyClassName}>
        <SearchHighlight isKey text={propertyKey} />
      </div>
      {changeType === EntityPropertyChanges.Added && (
        <div>
          <SearchHighlight text={newText} />
        </div>
      )}
      {changeType === EntityPropertyChanges.Removed && (
        <div className="entityProperty__changes">
          <SearchHighlight
            className="entityProperty__removed-value"
            text={removedText}
          />
          <span className="entityProperty__items-label">
            ({t("auditLog.changes.valueRemoved")})
          </span>
        </div>
      )}
      {changeType === EntityPropertyChanges.ItemsChanged && items && (
        <EntityPropertyItemsChange items={items} />
      )}
      {changeType === EntityPropertyChanges.Changed && (
        <div className="entityProperty__changes">
          {oldText !== undefined && (
            <>
              <div className="entityProperty__old-value">
                <SearchHighlight text={oldText} />
              </div>
              {"->"}
            </>
          )}
          <div className="entityProperty__new-value">
            <SearchHighlight text={newText} />
          </div>
          {numericDelta && (
            <div className="entityProperty__delta">
              {numericDelta.deltas.map((delta, index) => (
                <span key={index} className={getAuditLogDeltaClassName(delta)}>
                  {formatAuditLogDelta(delta)}
                </span>
              ))}
              {numericDelta.deltas.length > 1 && (
                <span className={getAuditLogDeltaClassName(numericDelta.total)}>
                  Σ {formatAuditLogDelta(numericDelta.total)}
                  {numericDelta.percent !== undefined &&
                    ` (${formatAuditLogDelta(numericDelta.percent)}%)`}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
